/**
 * Map loader — parses painter-format JSON per MAP.md §4.
 *
 * v0M2.4 supports the `raw-u8` encoding for terrain and provinceMap
 * (base64 of the raw byte stream, 1 byte / cell). The `raw-u16` and
 * `rle` variants are deferred as v0M2.4a candidates when maps grow
 * beyond 40×25 / 255 provinces.
 *
 * Fields not yet consumed by the v0M2.4 renderer (rivers, resources,
 * starts) are surfaced as-is on the LoadedMap so downstream milestones
 * pick them up without a schema retrip.
 */

import { assertSchemaVersion, MAP_SCHEMA_VERSION } from '../../data/loader';

// ---------------------------------------------------------------------------
// Painter JSON — over-the-wire shape
// ---------------------------------------------------------------------------

interface RawMeta {
  readonly id: string;
  readonly name: string;
  readonly size: readonly [number, number];
  readonly era: string;
}

/** v0M2.6c city kinds. Minor-only. Major cities have no kind. */
export type MinorCityKind = 'mine' | 'farm' | 'workshop' | 'trade_post' | 'shrine';

export interface RawMajorCity {
  readonly cell: readonly [number, number];
  readonly name: string;
  readonly pop: number;
}

export interface RawMinorCity {
  readonly cell: readonly [number, number];
  readonly name: string;
  readonly pop: number;
  readonly kind: MinorCityKind;
}

interface RawProvince {
  readonly id: number;
  readonly name: string;
  /** Backwards-compat convenience — mirrors majorCity.cell. Kept as legacy field. */
  readonly citySite: readonly [number, number];
  readonly pop: number;
  readonly region: string;
  /** v0M2.6c — major city (political capital of the province). */
  readonly majorCity?: RawMajorCity;
  /** v0M2.6c — 1-3 minor cities per province at v1. */
  readonly minorCities?: readonly RawMinorCity[];
}

interface RawRiverEdge {
  readonly edges: ReadonlyArray<readonly [number, number, 'E' | 'S']>;
}

interface RawResource {
  readonly type: string;
  readonly cell: readonly [number, number];
}

interface RawStart {
  readonly faction: string;
  readonly provinces: readonly number[];
  readonly hero?: boolean;
}

interface RawMapBlob {
  readonly schemaVersion: number;
  readonly meta: RawMeta;
  readonly terrainEncoding: 'raw-u8';
  readonly terrain: string;
  readonly provinceEncoding: 'raw-u8';
  readonly provinceMap: string;
  readonly rivers: readonly RawRiverEdge[];
  readonly provinces: readonly RawProvince[];
  readonly resources: readonly RawResource[];
  readonly starts: readonly RawStart[];
}

// ---------------------------------------------------------------------------
// Runtime types
// ---------------------------------------------------------------------------

export interface LoadedMap {
  readonly id: string;
  readonly name: string;
  readonly width: number;
  readonly height: number;
  readonly era: string;
  /** Row-major terrain-id grid, length = width × height. */
  readonly terrain: Uint8Array;
  /** Row-major province-id grid; 0 = unassigned. */
  readonly provinceMap: Uint8Array;
  readonly provinces: readonly RawProvince[];
  readonly rivers: readonly RawRiverEdge[];
  readonly resources: readonly RawResource[];
  readonly starts: readonly RawStart[];
}

// ---------------------------------------------------------------------------
// Loader entry points
// ---------------------------------------------------------------------------

/**
 * Fetch and parse a painter-format map from a URL (e.g. `/maps/karth-sample.json`).
 * Throws on schema mismatch, unsupported encoding, or wrong-size byte payloads.
 */
export async function loadMapFromUrl(url: string): Promise<LoadedMap> {
  const resp = await fetch(url);
  if (!resp.ok) {
    throw new Error(`Map fetch failed: ${resp.status} ${resp.statusText} for ${url}`);
  }
  const raw = (await resp.json()) as unknown;
  return parseMapBlob(raw, url);
}

/**
 * Parse an in-memory blob (test fixtures, painter tool). Same validation
 * path as loadMapFromUrl.
 */
export function parseMapBlob(raw: unknown, path = '<in-memory>'): LoadedMap {
  assertSchemaVersion(raw, MAP_SCHEMA_VERSION, path);
  const blob = raw as RawMapBlob;

  if (blob.terrainEncoding !== 'raw-u8') {
    throw new Error(
      `Unsupported terrainEncoding '${blob.terrainEncoding}' in ${path}. v0M2.4 supports 'raw-u8' only.`,
    );
  }
  if (blob.provinceEncoding !== 'raw-u8') {
    throw new Error(
      `Unsupported provinceEncoding '${blob.provinceEncoding}' in ${path}. v0M2.4 supports 'raw-u8' only.`,
    );
  }

  const [width, height] = blob.meta.size;
  const expectedBytes = width * height;

  const terrain = decodeBase64(blob.terrain);
  if (terrain.length !== expectedBytes) {
    throw new Error(
      `Terrain byte count mismatch in ${path}: expected ${expectedBytes}, got ${terrain.length}`,
    );
  }
  const provinceMap = decodeBase64(blob.provinceMap);
  if (provinceMap.length !== expectedBytes) {
    throw new Error(
      `provinceMap byte count mismatch in ${path}: expected ${expectedBytes}, got ${provinceMap.length}`,
    );
  }

  return {
    id: blob.meta.id,
    name: blob.meta.name,
    width,
    height,
    era: blob.meta.era,
    terrain,
    provinceMap,
    provinces: blob.provinces,
    rivers: blob.rivers,
    resources: blob.resources,
    starts: blob.starts,
  };
}

// ---------------------------------------------------------------------------
// Cell accessors
// ---------------------------------------------------------------------------

/** Terrain id at (x, y). Out-of-bounds throws. */
export function terrainAt(map: LoadedMap, x: number, y: number): number {
  if (x < 0 || y < 0 || x >= map.width || y >= map.height) {
    throw new Error(`terrainAt: (${x}, ${y}) out of bounds ${map.width}×${map.height}`);
  }
  return map.terrain[y * map.width + x]!;
}

/** Province id at (x, y). 0 = unassigned. */
export function provinceAt(map: LoadedMap, x: number, y: number): number {
  if (x < 0 || y < 0 || x >= map.width || y >= map.height) {
    throw new Error(`provinceAt: (${x}, ${y}) out of bounds ${map.width}×${map.height}`);
  }
  return map.provinceMap[y * map.width + x]!;
}

// ---------------------------------------------------------------------------
// Base64 → Uint8Array (browser + node compatible)
// ---------------------------------------------------------------------------

function decodeBase64(b64: string): Uint8Array {
  if (typeof atob === 'function') {
    // Browser path
    const binary = atob(b64);
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
    return out;
  }
  // Node path (tests)
  return new Uint8Array(Buffer.from(b64, 'base64'));
}
